/**
 * Field Ops Store
 * Zustand store for field ops UI state only.
 * All data comes from TanStack Query hooks (useWalkbooks, useShifts, etc.)
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { VoterMapFilter } from '@signalvote/shared';
import type { DemographicMetric } from '@/components/field-ops/DemographicOverlay';

/**
 * The layers a user can switch on and off from the Layers panel. Listing them
 * explicitly (rather than deriving "every boolean in the store") keeps internal
 * flags like isLoadingMajorRoads and layersPanelOpen out of reach of toggle().
 */
export type LayerFlag =
  | 'showVoterPins'
  | 'showTurfs'
  | 'showContactRate'
  | 'showDemographics'
  | 'showElectionOverlay'
  | 'showMajorRoads'
  | 'showLiveCanvassers';

interface FieldOpsState {
  /** Tracks the ID of the most recently created turf for post-save auto-selection */
  lastCreatedTurfId: string | null;

  // UI state
  mode: 'management' | 'cutting';
  selectedTurfId: string | null;
  turfListExpanded: boolean;
  /** Open state of the MapLayerPanel dropdown — controllable so the turf walkthrough can drive it. */
  layersPanelOpen: boolean;
  showLiveCanvassers: boolean;

  // Voter filter state
  voterFilters: VoterMapFilter;

  // Layer visibility state
  showVoterPins: boolean;
  showTurfs: boolean;
  showContactRate: boolean;

  // Data overlay state
  showDemographics: boolean;
  demographicMetric: DemographicMetric;
  showElectionOverlay: boolean;

  // Sign & Table Planning overlays
  showMajorRoads: boolean;
  /** Set by RoadClassOverlay during in-flight Overpass fetches; read by the floating legend. */
  isLoadingMajorRoads: boolean;

  // UI actions
  setMode: (mode: 'management' | 'cutting') => void;
  selectTurf: (turfId: string | null) => void;
  setTurfListExpanded: (expanded: boolean) => void;
  setLayersPanelOpen: (open: boolean) => void;
  setLastCreatedTurfId: (id: string | null) => void;

  // Voter filter actions
  setVoterFilters: (filters: Partial<VoterMapFilter>) => void;
  clearVoterFilters: () => void;

  /** Flip one layer on or off. */
  toggle: (flag: LayerFlag) => void;
  setDemographicMetric: (metric: DemographicMetric) => void;
  setLoadingMajorRoads: (loading: boolean) => void;
}

export const useFieldOpsStore = create<FieldOpsState>()(
  devtools(
    (set) => ({
      lastCreatedTurfId: null,

      // UI state
      mode: 'management',
      selectedTurfId: null,
      turfListExpanded: false,
      layersPanelOpen: false,
      showLiveCanvassers: true,

      // Voter filter state
      voterFilters: {} as VoterMapFilter,

      // Layer visibility state
      showVoterPins: true,
      showTurfs: true,
      showContactRate: false,

      // Data overlay state
      showDemographics: false,
      demographicMetric: 'medianIncome' as DemographicMetric,
      showElectionOverlay: false,

      // Sign & Table Planning overlays
      showMajorRoads: false,
      isLoadingMajorRoads: false,

      // UI actions
      setMode: (mode) => {
        set({ mode });
      },

      selectTurf: (turfId) => {
        set({ selectedTurfId: turfId, ...(turfId ? { turfListExpanded: true } : {}) });
      },

      setTurfListExpanded: (expanded) => {
        set({ turfListExpanded: expanded });
      },

      setLayersPanelOpen: (open) => {
        set({ layersPanelOpen: open });
      },

      setLastCreatedTurfId: (id) => {
        set({ lastCreatedTurfId: id });
      },

      // Voter filter actions
      setVoterFilters: (filters) => {
        set((state) => ({ voterFilters: { ...state.voterFilters, ...filters } }));
      },

      clearVoterFilters: () => {
        set({ voterFilters: {} });
      },

      // Layer visibility
      toggle: (flag) => {
        set((state) => ({ [flag]: !state[flag] }));
      },

      setDemographicMetric: (metric) => {
        set({ demographicMetric: metric });
      },

      setLoadingMajorRoads: (loading) => {
        set({ isLoadingMajorRoads: loading });
      },
    }),
    { name: 'field-ops-store' }
  )
);
